"use client";

import Image from "next/image";
import { useState } from "react";
import { getSession, signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { startAuthentication } from "@simplewebauthn/browser";
import { ThemeToggle } from "@/components/ThemeToggle";

type TwoFactorKind = "totp" | "sms" | null;

export default function LoginPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [twoFactor, setTwoFactor] = useState<TwoFactorKind>(null);
  const [smsHint, setSmsHint] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const needs2fa = twoFactor !== null;

  async function completeSignIn(opts?: { totpCode?: string; passkeyToken?: string }) {
    const result = await signIn("credentials", {
      email: opts?.passkeyToken ? "" : email,
      password: opts?.passkeyToken ? "" : password,
      totp: opts?.totpCode || "",
      passkeyToken: opts?.passkeyToken || "",
      redirect: false,
    });

    if (result?.error) {
      const err = result.error;
      if (opts?.passkeyToken) {
        setError(
          err === "CredentialsSignin"
            ? "Passkey sisselogimine ebaõnnestus"
            : `Sisselogimine ebaõnnestus (${err})`
        );
      } else if (needs2fa || opts?.totpCode) {
        setError(
          err === "CredentialsSignin"
            ? "Vale autentimiskood"
            : `Sisselogimine ebaõnnestus (${err})`
        );
      } else {
        setError(
          err === "CredentialsSignin" || err === "MissingCSRF"
            ? err === "MissingCSRF"
              ? "Turvaküpsis puudub — värskenda lehte (või kustuta saidi küpsised) ja proovi uuesti."
              : "Vale e-post või parool"
            : `Sisselogimine ebaõnnestus (${err})`
        );
      }
      return false;
    }

    const session = await getSession();
    if (!session?.user) {
      setError(
        "Sessiooni ei loodud. Proovi lehte värskendada või kustuta küpsised selle saidi jaoks."
      );
      return false;
    }

    router.replace("/");
    router.refresh();
    return true;
  }

  async function onPasskeyLogin() {
    setBusy(true);
    setError("");
    try {
      if (!window.isSecureContext) {
        setError("Passkey nõuab HTTPS-i (praegu pole turvaline kontekst).");
        setBusy(false);
        return;
      }

      const optRes = await fetch("/api/auth/passkey/login/options", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() || undefined }),
      });
      const options = await optRes.json().catch(() => ({}));
      if (!optRes.ok) {
        setError(options.error || "Passkey valikute laadimine ebaõnnestus");
        setBusy(false);
        return;
      }

      const assertion = await startAuthentication(options);
      const verRes = await fetch("/api/auth/passkey/login/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ response: assertion }),
      });
      const ver = await verRes.json().catch(() => ({}));
      if (!verRes.ok || !ver.passkeyToken) {
        setError(ver.error || "Passkey kontroll ebaõnnestus");
        setBusy(false);
        return;
      }

      const ok = await completeSignIn({ passkeyToken: ver.passkeyToken });
      if (!ok) setBusy(false);
    } catch (err) {
      console.error(err);
      const name = err && typeof err === "object" && "name" in err ? String(err.name) : "";
      if (name === "NotAllowedError") {
        setError("Passkey tühistati või pole saadaval.");
      } else {
        setError("Passkey sisselogimine ebaõnnestus");
      }
      setBusy(false);
    }
  }

  async function requestPrelogin(resendSms = false) {
    const pre = await fetch("/api/auth/prelogin", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, resendSms }),
    });
    const preData = await pre.json().catch(() => ({}));
    return { pre, preData };
  }

  async function onResendSms() {
    setBusy(true);
    setError("");
    try {
      const { pre, preData } = await requestPrelogin(true);
      if (preData.twoFactor === "sms") {
        setTwoFactor("sms");
        if (preData.smsHint) setSmsHint(preData.smsHint);
      }
      if (!pre.ok && !preData.twoFactor) {
        setError(preData.error || "SMS saatmine ebaõnnestus");
      } else if (preData.error) {
        setError(preData.error);
      } else if (preData.smsSent) {
        setError("");
      }
    } catch (err) {
      console.error(err);
      setError("Ühenduse viga — proovi uuesti");
    } finally {
      setBusy(false);
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (needs2fa) {
        const ok = await completeSignIn({ totpCode: totp.trim() });
        if (!ok) setBusy(false);
        return;
      }

      const { pre, preData } = await requestPrelogin(false);
      if (!pre.ok) {
        setError(preData.error || "Vale e-post või parool");
        setBusy(false);
        return;
      }

      if (preData.requires2fa) {
        const kind: TwoFactorKind =
          preData.twoFactor === "sms" ? "sms" : "totp";
        setTwoFactor(kind);
        if (kind === "sms" && preData.smsHint) {
          setSmsHint(String(preData.smsHint));
        }
        if (preData.error) setError(String(preData.error));
        setBusy(false);
        return;
      }

      const ok = await completeSignIn();
      if (!ok) setBusy(false);
    } catch (err) {
      console.error(err);
      setError("Ühenduse viga — proovi uuesti");
      setBusy(false);
    }
  }

  const hintText =
    twoFactor === "sms"
      ? smsHint
        ? `Saatsime 6-kohalise koodi numbrile ${smsHint}.`
        : "Saatsime 6-kohalise koodi SMS-iga."
      : twoFactor === "totp"
        ? "Sisesta autentimisrakenduse 6-kohaline kood."
        : "Logi sisse tehniku või administraatori kontoga.";

  return (
    <div className="relative flex min-h-dvh items-center justify-center px-4 py-8">
      <div className="absolute right-4 top-4 sm:right-6 sm:top-6">
        <ThemeToggle compact />
      </div>
      <div className="panel w-full max-w-md overflow-hidden rounded-2xl">
        <div className="bg-[#0a0a0a] px-6 py-6 sm:px-8 sm:py-7">
          <Image
            src="/brand/securer-logo.png"
            alt="Securer Videovalve"
            width={280}
            height={84}
            className="mx-auto h-12 w-auto sm:h-14"
            priority
          />
        </div>
        <div className="p-6 sm:p-8">
          <h1 className="font-[family-name:var(--font-display)] text-2xl text-ink">
            Piletisüsteem
          </h1>
          <p className="mt-2 text-sm text-muted">{hintText}</p>
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            {!needs2fa ? (
              <>
                <label className="block space-y-1 text-sm">
                  <span>E-post</span>
                  <input
                    className="input"
                    type="email"
                    inputMode="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </label>
                <label className="block space-y-1 text-sm">
                  <span>Parool</span>
                  <input
                    className="input"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </label>
              </>
            ) : (
              <label className="block space-y-1 text-sm">
                <span>
                  {twoFactor === "sms" ? "SMS kontrollkood" : "Autentimiskood"}
                </span>
                <input
                  className="input tracking-widest"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={totp}
                  onChange={(e) =>
                    setTotp(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  required
                  autoFocus
                />
              </label>
            )}
            {error && <p className="text-sm text-[#9f1239]">{error}</p>}
            <button className="btn btn-primary w-full min-h-12" disabled={busy} type="submit">
              {busy ? "Sisenen..." : needs2fa ? "Kinnita" : "Sisene"}
            </button>
            {!needs2fa && (
              <button
                type="button"
                className="btn w-full min-h-11"
                disabled={busy}
                onClick={() => void onPasskeyLogin()}
              >
                Logi sisse passkey’ga
              </button>
            )}
            {twoFactor === "sms" && (
              <button
                type="button"
                className="btn w-full min-h-11"
                disabled={busy}
                onClick={() => void onResendSms()}
              >
                Saada SMS uuesti
              </button>
            )}
            {needs2fa && (
              <button
                type="button"
                className="btn w-full min-h-11"
                disabled={busy}
                onClick={() => {
                  setTwoFactor(null);
                  setSmsHint("");
                  setTotp("");
                  setError("");
                }}
              >
                Tagasi
              </button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
